/**
 * Global Governance Gap Assessment Page — M6.3 Phase 2
 * Assessment CRUD, control evaluation, live posture scoring, gap findings, activity log
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API, useAuth } from '../App';
import QMSLayout from '../components/QMSLayout';
import {
  Shield, Plus, Search, Filter, Loader2, AlertTriangle,
  ChevronDown, ChevronUp, ChevronLeft, Eye, Clock,
  CheckCircle2, XCircle, MinusCircle, Ban, FileText,
  BarChart3, Activity, Layers, Database, Lock, Server, Bell,
  ShieldCheck, Target, ArrowRight, RefreshCw, TrendingUp
} from 'lucide-react';

const DOMAIN_CONFIG = {
  data_integrity: { icon: Database, color: 'text-cyan-400', bg: 'bg-cyan-500/15', border: 'border-cyan-500/30', label: 'Data Integrity' },
  cybersecurity: { icon: Lock, color: 'text-red-400', bg: 'bg-red-500/15', border: 'border-red-500/30', label: 'Cybersecurity' },
  governance: { icon: ShieldCheck, color: 'text-blue-400', bg: 'bg-blue-500/15', border: 'border-blue-500/30', label: 'Governance Controls' },
  resilience: { icon: Server, color: 'text-emerald-400', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', label: 'Resilience / Recovery' },
  incident_notification: { icon: Bell, color: 'text-amber-400', bg: 'bg-amber-500/15', border: 'border-amber-500/30', label: 'Incident / Notification' },
};

const SEVERITY_CONFIG = {
  critical: { bg: 'bg-red-500/15', text: 'text-red-400', label: 'Critical' },
  high: { bg: 'bg-orange-500/15', text: 'text-orange-400', label: 'High' },
  medium: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', label: 'Medium' },
  low: { bg: 'bg-green-500/15', text: 'text-green-400', label: 'Low' },
};

const STATUS_CONFIG = {
  draft: { bg: 'bg-slate-500/15', text: 'text-slate-400', label: 'Draft' },
  in_progress: { bg: 'bg-blue-500/15', text: 'text-blue-400', label: 'In Progress' },
  completed: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', label: 'Completed' },
  archived: { bg: 'bg-gray-500/15', text: 'text-gray-400', label: 'Archived' },
};

const EVAL_OPTIONS = [
  { value: 'met', label: 'Met', icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/15', border: 'border-emerald-500/40' },
  { value: 'partial', label: 'Partial', icon: MinusCircle, color: 'text-yellow-400', bg: 'bg-yellow-500/15', border: 'border-yellow-500/40' },
  { value: 'unmet', label: 'Unmet', icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/15', border: 'border-red-500/40' },
  { value: 'not_applicable', label: 'N/A', icon: Ban, color: 'text-gray-400', bg: 'bg-gray-500/15', border: 'border-gray-500/40' },
];

const FINDING_TYPES = {
  gap: { label: 'Gap', color: 'text-red-400' },
  partial_gap: { label: 'Partial Gap', color: 'text-orange-400' },
  observation: { label: 'Observation', color: 'text-yellow-400' },
  strength: { label: 'Strength', color: 'text-emerald-400' },
};

const REGIONS = ['US', 'EU', 'CA', 'BR', 'LATAM', 'AU', 'JP', 'CN', 'UK', 'OUS'];
const DOMAINS = ['data_integrity', 'cybersecurity', 'governance', 'resilience', 'incident_notification'];

// =============== SMALL COMPONENTS ===============

const StatusBadge = ({ status, config }) => {
  const cfg = (config || STATUS_CONFIG)[status] || { bg: 'bg-gray-500/15', text: 'text-gray-400', label: status };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.bg} ${cfg.text}`} data-testid={`status-badge-${status}`}>
      {cfg.label}
    </span>
  );
};

const ScoreBand = ({ score, band }) => {
  let color = 'text-red-400';
  let bg = 'bg-red-500/15';
  if (score >= 85) { color = 'text-emerald-400'; bg = 'bg-emerald-500/15'; }
  else if (score >= 70) { color = 'text-blue-400'; bg = 'bg-blue-500/15'; }
  else if (score >= 50) { color = 'text-yellow-400'; bg = 'bg-yellow-500/15'; }

  return (
    <div className="flex items-center gap-2" data-testid="score-band">
      <span className={`text-2xl font-bold ${color}`}>{score}%</span>
      <span className={`text-xs px-2 py-0.5 rounded ${bg} ${color}`}>{band}</span>
    </div>
  );
};

const ScoreBar = ({ score, label, color }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-xs">
      <span className="text-[#8B95A5]">{label}</span>
      <span className={color || 'text-white'}>{score}%</span>
    </div>
    <div className="h-1.5 bg-[rgba(255,255,255,0.05)] rounded-full overflow-hidden">
      <div className={`h-full rounded-full transition-all duration-500 ${score >= 85 ? 'bg-emerald-500' : score >= 70 ? 'bg-blue-500' : score >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${Math.max(score, 2)}%` }} />
    </div>
  </div>
);

// =============== CREATE ASSESSMENT MODAL ===============

const CreateAssessmentModal = ({ onClose, onCreated }) => {
  const { t } = useTranslation(['governance', 'common', 'validation']);
  const [title, setTitle] = useState('');
  const [scope, setScope] = useState('');
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleRegion = (r) => setSelectedRegions(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  const toggleDomain = (d) => setSelectedDomains(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);

  const handleCreate = async () => {
    if (!title.trim()) { setError(t('common:messages.requiredField')); return; }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/governance/assessments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: title.trim(),
          scope: scope.trim(),
          regions: selectedRegions.length ? selectedRegions : [],
          domains: selectedDomains.length ? selectedDomains : [],
        }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || t('common:messages.operationFailed'));
      }
      const data = await res.json();
      onCreated(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="create-assessment-modal">
      <div className="bg-[#1A1F2B] border border-[rgba(255,255,255,0.08)] rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[rgba(255,255,255,0.05)]">
          <h3 className="text-white text-lg font-semibold">{t('governance:createModal.title')}</h3>
          <p className="text-[#8B95A5] text-xs mt-1">{t('governance:createModal.description')}</p>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded p-2" data-testid="create-error">{error}</div>}
          <div>
            <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:createModal.assessmentTitle')} *</label>
            <input
              type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder={t('governance:createModal.titlePlaceholder')}
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm placeholder-[#555] focus:border-[#36A9E1] focus:outline-none"
              data-testid="assessment-title-input"
            />
          </div>
          <div>
            <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:createModal.scopeLabel')}</label>
            <textarea
              value={scope} onChange={e => setScope(e.target.value)} rows={3} placeholder={t('governance:createModal.scopePlaceholder')}
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm placeholder-[#555] focus:border-[#36A9E1] focus:outline-none resize-none"
              data-testid="assessment-scope-input"
            />
          </div>
          <div>
            <label className="block text-[#8B95A5] text-xs mb-2">{t('governance:createModal.regionsLabel')}</label>
            <div className="flex flex-wrap gap-1.5">
              {REGIONS.map(r => (
                <button key={r} onClick={() => toggleRegion(r)}
                  className={`px-2.5 py-1 rounded text-xs font-mono transition-colors ${selectedRegions.includes(r) ? 'bg-[#36A9E1]/20 text-[#36A9E1] border border-[#36A9E1]/40' : 'bg-[rgba(255,255,255,0.05)] text-[#8B95A5] border border-transparent hover:border-[rgba(255,255,255,0.1)]'}`}
                  data-testid={`region-toggle-${r}`}
                >{r}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[#8B95A5] text-xs mb-2">{t('governance:createModal.domainsLabel')}</label>
            <div className="flex flex-wrap gap-1.5">
              {DOMAINS.map(d => {
                const cfg = DOMAIN_CONFIG[d];
                return (
                  <button key={d} onClick={() => toggleDomain(d)}
                    className={`px-2.5 py-1 rounded text-xs transition-colors ${selectedDomains.includes(d) ? `${cfg.bg} ${cfg.color} border ${cfg.border}` : 'bg-[rgba(255,255,255,0.05)] text-[#8B95A5] border border-transparent hover:border-[rgba(255,255,255,0.1)]'}`}
                    data-testid={`domain-toggle-${d}`}
                  >{cfg.label}</button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-[rgba(255,255,255,0.05)] flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-[#8B95A5] text-sm hover:text-white transition-colors" data-testid="cancel-create-btn">{t('common:actions.cancel')}</button>
          <button onClick={handleCreate} disabled={saving}
            className="px-4 py-2 rounded-lg bg-[#36A9E1] text-white text-sm font-medium hover:bg-[#2b8bbf] disabled:opacity-50 transition-colors flex items-center gap-2"
            data-testid="confirm-create-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {t('governance:createModal.createButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============== CREATE FINDING MODAL ===============

const CreateFindingModal = ({ assessmentId, controls, onClose, onCreated }) => {
  const { t } = useTranslation(['governance', 'common']);
  const [controlId, setControlId] = useState('');
  const [region, setRegion] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [findingType, setFindingType] = useState('gap');
  const [condition, setCondition] = useState('');
  const [riskImpact, setRiskImpact] = useState('');
  const [remediation, setRemediation] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    if (!controlId) { setError(t('common:messages.selectOption')); return; }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/governance/assessments/${assessmentId}/findings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          control_id: controlId, region, severity, finding_type: findingType,
          condition_statement: condition.trim(), risk_impact: riskImpact.trim(),
          recommended_remediation: remediation.trim(),
        }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || t('common:messages.operationFailed'));
      }
      const data = await res.json();
      onCreated(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="create-finding-modal">
      <div className="bg-[#1A1F2B] border border-[rgba(255,255,255,0.08)] rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[rgba(255,255,255,0.05)]">
          <h3 className="text-white text-lg font-semibold">{t('governance:findingModal.title')}</h3>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded p-2" data-testid="finding-error">{error}</div>}
          <div>
            <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:findingModal.controlLabel')} *</label>
            <select value={controlId} onChange={e => setControlId(e.target.value)}
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm focus:border-[#36A9E1] focus:outline-none"
              data-testid="finding-control-select"
            >
              <option value="">{t('governance:findingModal.controlPlaceholder')}</option>
              {controls.map(c => <option key={c.control_id} value={c.control_id}>{c.control_code} — {c.title}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:findingModal.severityLabel')}</label>
              <select value={severity} onChange={e => setSeverity(e.target.value)}
                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm focus:border-[#36A9E1] focus:outline-none"
                data-testid="finding-severity-select"
              >
                {Object.entries(SEVERITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:findingModal.typeLabel')}</label>
              <select value={findingType} onChange={e => setFindingType(e.target.value)}
                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm focus:border-[#36A9E1] focus:outline-none"
                data-testid="finding-type-select"
              >
                {Object.entries(FINDING_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:findingModal.regionLabel')}</label>
              <select value={region} onChange={e => setRegion(e.target.value)}
                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm focus:border-[#36A9E1] focus:outline-none"
                data-testid="finding-region-select"
              >
                <option value="">{t('governance:findingModal.globalOption')}</option>
                {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:findingModal.conditionLabel')}</label>
            <textarea value={condition} onChange={e => setCondition(e.target.value)} rows={2} placeholder={t('governance:findingModal.conditionPlaceholder')}
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm placeholder-[#555] focus:border-[#36A9E1] focus:outline-none resize-none"
              data-testid="finding-condition-input"
            />
          </div>
          <div>
            <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:findingModal.riskLabel')}</label>
            <textarea value={riskImpact} onChange={e => setRiskImpact(e.target.value)} rows={2} placeholder={t('governance:findingModal.riskPlaceholder')}
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm placeholder-[#555] focus:border-[#36A9E1] focus:outline-none resize-none"
              data-testid="finding-risk-input"
            />
          </div>
          <div>
            <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:findingModal.remediationLabel')}</label>
            <textarea value={remediation} onChange={e => setRemediation(e.target.value)} rows={2} placeholder={t('governance:findingModal.remediationPlaceholder')}
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm placeholder-[#555] focus:border-[#36A9E1] focus:outline-none resize-none"
              data-testid="finding-remediation-input"
            />
          </div>
        </div>
        <div className="p-6 border-t border-[rgba(255,255,255,0.05)] flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-[#8B95A5] text-sm hover:text-white transition-colors" data-testid="cancel-finding-btn">{t('common:actions.cancel')}</button>
          <button onClick={handleCreate} disabled={saving}
            className="px-4 py-2 rounded-lg bg-[#36A9E1] text-white text-sm font-medium hover:bg-[#2b8bbf] disabled:opacity-50 transition-colors flex items-center gap-2"
            data-testid="confirm-finding-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {t('governance:findingModal.recordButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============== ASSESSMENT DETAIL VIEW ===============

const AssessmentDetail = ({ assessmentId, onBack }) => {
  const { isAdmin } = useAuth();
  const { t } = useTranslation(['governance', 'common']);
  const [assessment, setAssessment] = useState(null);
  const [controls, setControls] = useState([]);
  const [findings, setFindings] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('evaluations');
  const [evaluating, setEvaluating] = useState({});
  const [domainFilter, setDomainFilter] = useState('all');
  const [showCreateFinding, setShowCreateFinding] = useState(false);
  const [findingSeverityFilter, setFindingSeverityFilter] = useState('all');
  const [findingStatusFilter, setFindingStatusFilter] = useState('all');

  const loadAssessment = useCallback(async () => {
    try {
      const [aRes, cRes, fRes, actRes] = await Promise.all([
        fetch(`${API}/governance/assessments/${assessmentId}`, { credentials: 'include' }),
        fetch(`${API}/governance/frameworks/controls`, { credentials: 'include' }),
        fetch(`${API}/governance/assessments/${assessmentId}/findings`, { credentials: 'include' }),
        fetch(`${API}/governance/assessments/${assessmentId}/activity`, { credentials: 'include' }),
      ]);
      if (!aRes.ok) throw new Error('Failed to load assessment');
      const [aData, cData, fData, actData] = await Promise.all([
        aRes.json(), cRes.json(), fRes.json(), actRes.json(),
      ]);
      setAssessment(aData);
      setControls(cData);
      setFindings(fData);
      setActivity(actData);
    } catch (err) {
      console.error('Error loading assessment:', err);
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => { loadAssessment(); }, [loadAssessment]);

  const handleEvaluate = async (controlId, status, notes = '') => {
    setEvaluating(prev => ({ ...prev, [controlId]: true }));
    try {
      const res = await fetch(`${API}/governance/assessments/${assessmentId}/evaluate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ control_id: controlId, status, notes }),
      });
      if (!res.ok) throw new Error('Evaluation failed');
      await loadAssessment();
    } catch (err) {
      console.error('Evaluation error:', err);
    } finally {
      setEvaluating(prev => ({ ...prev, [controlId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-[#36A9E1]" size={32} />
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="qms-card">
        <div className="qms-card-body p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 text-red-400" size={48} />
          <p className="text-white">{t('governance:assessments.assessmentNotFound')}</p>
          <button onClick={onBack} className="mt-4 text-[#36A9E1] text-sm hover:underline" data-testid="back-to-list-btn">{t('governance:assessments.backToList')}</button>
        </div>
      </div>
    );
  }

  const evalMap = {};
  (assessment.evaluations || []).forEach(e => { evalMap[e.control_id] = e; });
  const score = assessment.score || {};

  // Filter controls to the assessment's domains
  const assessmentDomains = assessment.domains || DOMAINS;
  const scopedControls = controls.filter(c => assessmentDomains.includes(c.domain));
  const filteredControls = domainFilter === 'all' ? scopedControls : scopedControls.filter(c => c.domain === domainFilter);

  const filteredFindings = findings.filter(f => {
    if (findingSeverityFilter !== 'all' && f.severity !== findingSeverityFilter) return false;
    if (findingStatusFilter !== 'all' && f.status !== findingStatusFilter) return false;
    return true;
  });

  const TABS = [
    { id: 'evaluations', label: t('governance:tabs.controlEvaluations'), icon: Layers },
    { id: 'findings', label: `${t('governance:tabs.findings')} (${findings.length})`, icon: Target },
    { id: 'activity', label: t('governance:tabs.activityLog'), icon: Activity },
  ];

  return (
    <div className="space-y-6" data-testid="assessment-detail">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <button onClick={onBack} className="text-[#8B95A5] hover:text-white transition-colors" data-testid="back-btn">
          <ChevronLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h2 className="text-white text-lg font-semibold truncate">{assessment.title}</h2>
            <StatusBadge status={assessment.status} />
          </div>
          <p className="text-[#8B95A5] text-xs mt-0.5">
            {assessment.assessment_id} | {t('governance:detail.owner')}: {assessment.owner_name} | {t('governance:detail.created')}: {new Date(assessment.created_at).toLocaleDateString()}
          </p>
          {assessment.scope && <p className="text-[#8B95A5] text-xs mt-1 italic">{assessment.scope}</p>}
        </div>
        <button onClick={loadAssessment} className="text-[#8B95A5] hover:text-[#36A9E1] transition-colors p-1" data-testid="refresh-btn">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Posture Score Card */}
      <div className="qms-card" data-testid="posture-score-card">
        <div className="qms-card-body p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="text-[#36A9E1]" size={18} />
              <h3 className="text-white text-sm font-semibold">{t('governance:detail.livePostureScore')}</h3>
              <span className="text-[#555] text-xs">({t('governance:detail.deterministic')})</span>
            </div>
            <ScoreBand score={score.overall_score || 0} band={score.overall_band || 'N/A'} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-3 text-center">
              <div className="text-white text-lg font-bold" data-testid="total-controls">{score.total_controls || 0}</div>
              <div className="text-[#8B95A5] text-xs">{t('governance:detail.totalControls')}</div>
            </div>
            <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-3 text-center">
              <div className="text-emerald-400 text-lg font-bold" data-testid="met-count">{score.met_count || 0}</div>
              <div className="text-[#8B95A5] text-xs">{t('governance:detail.met')}</div>
            </div>
            <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-3 text-center">
              <div className="text-yellow-400 text-lg font-bold" data-testid="partial-count">{score.partial_count || 0}</div>
              <div className="text-[#8B95A5] text-xs">{t('governance:detail.partial')}</div>
            </div>
            <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-3 text-center">
              <div className="text-red-400 text-lg font-bold" data-testid="unmet-count">{score.unmet_count || 0}</div>
              <div className="text-[#8B95A5] text-xs">{t('governance:detail.unmet')}</div>
            </div>
          </div>
          {score.domain_scores && (
            <div className="space-y-2">
              {Object.entries(score.domain_scores).map(([d, ds]) => (
                <ScoreBar key={d} score={ds.score} label={ds.label} color={DOMAIN_CONFIG[d]?.color} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-1 bg-[rgba(255,255,255,0.03)] rounded-lg p-1" data-testid="detail-tabs">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors ${activeTab === tab.id ? 'bg-[#36A9E1]/15 text-[#36A9E1]' : 'text-[#8B95A5] hover:text-white'}`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Evaluations Tab */}
      {activeTab === 'evaluations' && (
        <div className="space-y-4" data-testid="evaluations-tab">
          <div className="flex items-center gap-3">
            <select value={domainFilter} onChange={e => setDomainFilter(e.target.value)}
              className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none"
              data-testid="eval-domain-filter"
            >
              <option value="all">{t('governance:evaluations.allDomains')}</option>
              {assessmentDomains.map(d => <option key={d} value={d}>{DOMAIN_CONFIG[d]?.label || d}</option>)}
            </select>
            <span className="text-[#8B95A5] text-xs">
              {score.evaluated_count || 0} {t('governance:detail.of')} {score.total_controls || 0} {t('governance:detail.evaluated')}
            </span>
          </div>
          <div className="space-y-2">
            {filteredControls.map(control => {
              const ev = evalMap[control.control_id];
              const currentStatus = ev?.status || null;
              const isEvaluating = evaluating[control.control_id];
              const domainCfg = DOMAIN_CONFIG[control.domain] || DOMAIN_CONFIG.governance;

              return (
                <div key={control.control_id} className="qms-card" data-testid={`eval-control-${control.control_id}`}>
                  <div className="qms-card-body p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${domainCfg.bg} ${domainCfg.color}`}>{control.control_code}</span>
                        <h4 className="text-white text-sm font-medium truncate">{control.title}</h4>
                      </div>
                      <StatusBadge status={control.severity_if_missing} config={SEVERITY_CONFIG} />
                    </div>
                    <p className="text-[#8B95A5] text-xs leading-relaxed mb-3">{control.description}</p>
                    <div className="flex items-center gap-2">
                      {EVAL_OPTIONS.map(opt => {
                        const Icon = opt.icon;
                        const isSelected = currentStatus === opt.value;
                        return (
                          <button key={opt.value}
                            onClick={() => handleEvaluate(control.control_id, opt.value)}
                            disabled={isEvaluating}
                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${isSelected ? `${opt.bg} ${opt.color} border ${opt.border}` : 'bg-[rgba(255,255,255,0.04)] text-[#8B95A5] border border-transparent hover:border-[rgba(255,255,255,0.1)] hover:text-white'}`}
                            data-testid={`eval-${control.control_id}-${opt.value}`}
                          >
                            {isEvaluating ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />}
                            {opt.label}
                          </button>
                        );
                      })}
                      {ev && (
                        <span className="text-[#555] text-xs ml-auto">
                          {t('governance:evaluations.evaluatedOn')} {new Date(ev.evaluated_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Findings Tab */}
      {activeTab === 'findings' && (
        <div className="space-y-4" data-testid="findings-tab">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <select value={findingSeverityFilter} onChange={e => setFindingSeverityFilter(e.target.value)}
                className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none"
                data-testid="finding-severity-filter"
              >
                <option value="all">All Severities</option>
                {Object.entries(SEVERITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select value={findingStatusFilter} onChange={e => setFindingStatusFilter(e.target.value)}
                className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none"
                data-testid="finding-status-filter"
              >
                <option value="all">{t('governance:findings.allStatuses')}</option>
                <option value="open">{t('governance:statuses.open')}</option>
                <option value="in_progress">{t('governance:statuses.in_progress')}</option>
                <option value="remediation_planned">{t('governance:statuses.remediation_planned')}</option>
                <option value="closed">{t('governance:statuses.closed')}</option>
                <option value="accepted_risk">{t('governance:statuses.accepted_risk')}</option>
              </select>
            </div>
            <button onClick={() => setShowCreateFinding(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#36A9E1] text-white text-xs font-medium hover:bg-[#2b8bbf] transition-colors"
              data-testid="add-finding-btn"
            >
              <Plus size={14} /> {t('governance:findings.recordFinding')}
            </button>
          </div>
          {filteredFindings.length === 0 ? (
            <div className="qms-card">
              <div className="qms-card-body p-8 text-center">
                <Target className="mx-auto mb-3 text-[#555]" size={32} />
                <p className="text-[#8B95A5] text-sm">{t('governance:findings.noFindingsTitle')}</p>
                <p className="text-[#555] text-xs mt-1">{t('governance:findings.noFindingsDesc')}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredFindings.map(f => (
                <div key={f.finding_id} className="qms-card" data-testid={`finding-card-${f.finding_id}`}>
                  <div className="qms-card-body p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-mono text-[#36A9E1] bg-[rgba(54,169,225,0.1)] px-1.5 py-0.5 rounded">{f.finding_id}</span>
                        <span className="text-white text-sm font-medium truncate">{f.control_title || f.control_id}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <StatusBadge status={f.severity} config={SEVERITY_CONFIG} />
                        <span className={`text-xs ${FINDING_TYPES[f.finding_type]?.color || 'text-gray-400'}`}>{FINDING_TYPES[f.finding_type]?.label || f.finding_type}</span>
                        <StatusBadge status={f.status} config={{
                          open: { bg: 'bg-red-500/15', text: 'text-red-400', label: 'Open' },
                          in_progress: { bg: 'bg-blue-500/15', text: 'text-blue-400', label: 'In Progress' },
                          remediation_planned: { bg: 'bg-purple-500/15', text: 'text-purple-400', label: 'Remediation Planned' },
                          closed: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', label: 'Closed' },
                          accepted_risk: { bg: 'bg-orange-500/15', text: 'text-orange-400', label: 'Accepted Risk' },
                        }} />
                      </div>
                    </div>
                    {f.condition_statement && <p className="text-[#8B95A5] text-xs mb-1"><span className="text-[#555]">{t('governance:findings.condition')}:</span> {f.condition_statement}</p>}
                    {f.risk_impact && <p className="text-[#8B95A5] text-xs mb-1"><span className="text-[#555]">{t('governance:findings.risk')}:</span> {f.risk_impact}</p>}
                    {f.recommended_remediation && <p className="text-[#8B95A5] text-xs"><span className="text-[#555]">{t('governance:findings.remediation')}:</span> {f.recommended_remediation}</p>}
                    <div className="flex items-center gap-3 mt-2 text-[#555] text-xs">
                      {f.region && <span>{t('governance:findings.region')}: {f.region}</span>}
                      {f.domain && <span>{t('governance:findings.domain')}: {DOMAIN_CONFIG[f.domain]?.label || f.domain}</span>}
                      <span>{t('governance:detail.created')}: {new Date(f.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {showCreateFinding && (
            <CreateFindingModal
              assessmentId={assessmentId}
              controls={scopedControls}
              onClose={() => setShowCreateFinding(false)}
              onCreated={() => { setShowCreateFinding(false); loadAssessment(); }}
            />
          )}
        </div>
      )}

      {/* Activity Tab */}
      {activeTab === 'activity' && (
        <div className="space-y-2" data-testid="activity-tab">
          {activity.length === 0 ? (
            <div className="qms-card">
              <div className="qms-card-body p-8 text-center">
                <Activity className="mx-auto mb-3 text-[#555]" size={32} />
                <p className="text-[#8B95A5] text-sm">{t('governance:activity.noActivity')}</p>
              </div>
            </div>
          ) : (
            activity.map((a, idx) => (
              <div key={a.log_id || idx} className="flex items-start gap-3 py-2 border-b border-[rgba(255,255,255,0.03)] last:border-0" data-testid={`activity-entry-${idx}`}>
                <div className="w-1.5 h-1.5 rounded-full bg-[#36A9E1] mt-1.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-white text-xs">
                    <span className="text-[#36A9E1] font-medium">{a.actor_name}</span>
                    {' '}{a.event_type?.replace(/_/g, ' ')}
                    {a.entity_id && <span className="text-[#555]"> ({a.entity_id})</span>}
                  </p>
                  <p className="text-[#555] text-xs mt-0.5">{new Date(a.timestamp).toLocaleString()}</p>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// =============== MAIN PAGE ===============

const GlobalGovernanceAssessmentPage = () => {
  const { isAdmin, isAuditor } = useAuth();
  const { t } = useTranslation(['governance', 'common']);
  const [assessments, setAssessments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const [aRes, sRes] = await Promise.all([
        fetch(`${API}/governance/assessments${params}`, { credentials: 'include' }),
        fetch(`${API}/governance/assessments/stats`, { credentials: 'include' }),
      ]);
      if (aRes.status === 403) {
        setError('Access denied. This module is restricted to governance roles.');
        return;
      }
      if (!aRes.ok || !sRes.ok) throw new Error('Failed to load assessments');
      const [aData, sData] = await Promise.all([aRes.json(), sRes.json()]);
      setAssessments(aData);
      setStats(sData);
    } catch (err) {
      console.error('Error loading assessments:', err);
      setError('Failed to load assessments.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!isAdmin && !isAuditor) {
    return (
      <QMSLayout title={t('governance:tabs.controlEvaluations')}>
        <div className="qms-card">
          <div className="qms-card-body p-8 text-center">
            <Shield className="mx-auto mb-4 text-red-400" size={48} />
            <h3 className="text-white font-semibold mb-2">{t('governance:assessments.accessRestricted')}</h3>
            <p className="text-[#8B95A5] text-sm">{t('governance:assessments.accessRestrictedDesc')}</p>
          </div>
        </div>
      </QMSLayout>
    );
  }

  if (selectedAssessment) {
    return (
      <QMSLayout title={t('governance:assessments.pageTitle')}>
        <AssessmentDetail assessmentId={selectedAssessment} onBack={() => { setSelectedAssessment(null); loadData(); }} />
      </QMSLayout>
    );
  }

  return (
    <QMSLayout title={t('governance:assessments.pageTitle')}>
      <div className="space-y-6" data-testid="gap-assessments-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-white text-xl font-bold">{t('governance:assessments.pageTitle')}</h1>
            <p className="text-[#8B95A5] text-xs mt-1">{t('governance:assessments.pageDescription')}</p>
          </div>
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#36A9E1] text-white text-sm font-medium hover:bg-[#2b8bbf] transition-colors"
            data-testid="new-assessment-btn"
          >
            <Plus size={16} /> {t('governance:assessments.newAssessment')}
          </button>
        </div>

        {/* Stats Row */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="assessment-stats">
            <div className="qms-card">
              <div className="qms-card-body p-4 text-center">
                <div className="text-white text-2xl font-bold" data-testid="stat-total">{stats.total_assessments}</div>
                <div className="text-[#8B95A5] text-xs mt-1">{t('governance:assessments.totalAssessments')}</div>
              </div>
            </div>
            <div className="qms-card">
              <div className="qms-card-body p-4 text-center">
                <div className="text-blue-400 text-2xl font-bold" data-testid="stat-in-progress">{stats.assessment_statuses?.in_progress || 0}</div>
                <div className="text-[#8B95A5] text-xs mt-1">{t('governance:assessments.inProgress')}</div>
              </div>
            </div>
            <div className="qms-card">
              <div className="qms-card-body p-4 text-center">
                <div className="text-emerald-400 text-2xl font-bold" data-testid="stat-completed">{stats.assessment_statuses?.completed || 0}</div>
                <div className="text-[#8B95A5] text-xs mt-1">{t('governance:assessments.completed')}</div>
              </div>
            </div>
            <div className="qms-card">
              <div className="qms-card-body p-4 text-center">
                <div className="text-orange-400 text-2xl font-bold" data-testid="stat-findings">{stats.total_findings}</div>
                <div className="text-[#8B95A5] text-xs mt-1">{t('governance:assessments.totalFindings')}</div>
              </div>
            </div>
          </div>
        )}

        {/* Filter Bar */}
        <div className="flex items-center gap-3">
          <Filter size={14} className="text-[#8B95A5]" />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none"
            data-testid="status-filter"
          >
            <option value="all">{t('governance:assessments.allStatuses')}</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button onClick={loadData} className="text-[#8B95A5] hover:text-[#36A9E1] transition-colors p-1" data-testid="refresh-list-btn">
            <RefreshCw size={14} />
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-[#36A9E1]" size={32} />
          </div>
        ) : error ? (
          <div className="qms-card">
            <div className="qms-card-body p-8 text-center">
              <AlertTriangle className="mx-auto mb-4 text-red-400" size={48} />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          </div>
        ) : assessments.length === 0 ? (
          <div className="qms-card">
            <div className="qms-card-body p-12 text-center">
              <Shield className="mx-auto mb-4 text-[#555]" size={48} />
              <h3 className="text-white font-semibold mb-2">{t('governance:assessments.noAssessmentsTitle')}</h3>
              <p className="text-[#8B95A5] text-sm mb-4">{t('governance:assessments.noAssessmentsDesc')}</p>
              <button onClick={() => setShowCreate(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#36A9E1] text-white text-sm font-medium hover:bg-[#2b8bbf] transition-colors"
                data-testid="empty-state-create-btn"
              >
                <Plus size={16} /> {t('governance:assessments.createFirst')}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {assessments.map(a => (
              <div key={a.assessment_id} className="qms-card hover:border-[rgba(54,169,225,0.2)] transition-colors cursor-pointer"
                onClick={() => setSelectedAssessment(a.assessment_id)}
                data-testid={`assessment-card-${a.assessment_id}`}
              >
                <div className="qms-card-body p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-[#36A9E1] bg-[rgba(54,169,225,0.1)] px-1.5 py-0.5 rounded">{a.assessment_id}</span>
                        <StatusBadge status={a.status} />
                      </div>
                      <h3 className="text-white text-sm font-medium">{a.title}</h3>
                      {a.scope && <p className="text-[#8B95A5] text-xs mt-1 line-clamp-1">{a.scope}</p>}
                      <div className="flex items-center gap-4 mt-2 text-[#555] text-xs">
                        <span>{t('governance:detail.owner')}: {a.owner_name}</span>
                        <span>{t('governance:detail.regions')}: {a.regions?.length || 'All'}</span>
                        <span>{t('governance:detail.domains')}: {a.domains?.length || 'All'}</span>
                        <span>{t('governance:detail.created')}: {new Date(a.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <ArrowRight size={16} className="text-[#555] shrink-0 mt-2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showCreate && (
          <CreateAssessmentModal
            onClose={() => setShowCreate(false)}
            onCreated={() => { setShowCreate(false); loadData(); }}
          />
        )}
      </div>
    </QMSLayout>
  );
};

export default GlobalGovernanceAssessmentPage;
